import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { keyBy } from 'lodash/fp';
import { Fragment, useEffect, useMemo, VFC } from 'react';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { TriggerRecurringAttributes } from 'dashboard/models/trigger';
import { WorkflowFilterFieldConfig } from 'dashboard/models/workflow-schema';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { intersperse } from 'lib/utils/array';

const CriterionRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${Spacing[2]};
`;

const CriterionTarget = styled.div`
  flex-shrink: 0;
  width: 220px;
`;

const CriterionOperator = styled.div`
  flex-shrink: 0;
  width: 120px;
  margin-left: ${Spacing[2]};
`;

const CriterionValue = styled.div`
  flex-grow: 1;
  margin-left: ${Spacing[2]};
`;

const CriterionValueUnit = styled.div`
  width: 160px;
  margin-left: ${Spacing[2]};
`;

const CriterionDeleteButton = styled.div`
  margin-left: ${Spacing[2]};
`;

interface FilterConfigRowProps {
  control: Control<{
    criteria: TriggerRecurringAttributes['filter'];
  }>;
  editable: boolean;
  fieldConfig: WorkflowFilterFieldConfig['config'];
  idPrefix: string;
  index: number;
  criterionTargetOptions: { label: string; value: string }[];
  onDelete: () => void;
}

const NEW_CRITERIA: TriggerRecurringAttributes['filter'][number] = {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  name: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  operator: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  unit: undefined,
  value: '',
};

const FilterConfigRow: VFC<FilterConfigRowProps> = ({
  control,
  editable,
  fieldConfig,
  idPrefix,
  index,
  criterionTargetOptions,
  onDelete,
}) => (
  <CriterionRow>
    <CriterionTarget>
      <Controller
        control={control}
        name={`criteria.${index}.name`}
        rules={{
          required: true,
        }}
        render={({ field: { value, onChange } }) => (
          <ReactSelect
            aria-label={`Target of criteria ${index + 1}`}
            disabled={!editable}
            id={`${idPrefix}__filter-name-${index}`}
            options={criterionTargetOptions}
            placeholder='Field'
            portal
            value={value}
            onChange={onChange}
          />
        )}
      />
    </CriterionTarget>
    <CriterionOperator>
      <Controller
        control={control}
        name={`criteria.${index}.operator`}
        rules={{
          required: true,
        }}
        render={({ field: { value, onChange } }) => (
          <Select
            disabled={(fieldConfig?.operators ?? []).length === 0 || !editable}
            id={`${idPrefix}__filter-operator-${index}`}
            placeholder='Operator'
            value={
              fieldConfig?.operators.find(({ value: operatorValue }) => operatorValue === value)
                ?.value
            }
            onValueChange={onChange}
          >
            {fieldConfig?.operators.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        )}
      />
    </CriterionOperator>
    <Controller
      control={control}
      name={`criteria.${index}.value`}
      rules={{
        required: true,
      }}
      render={({ field: { value, onChange } }) =>
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fieldConfig?.operators ?? []).length > 0 && (
          <CriterionValue>
            {(fieldConfig.options ?? []).length !== 0 ? (
              <ReactSelect
                aria-label={`Select options of criteria ${index + 1}`}
                disabled={!editable}
                id={`${idPrefix}__filter-multi-value-${index}`}
                isMulti={fieldConfig.type === 'Multi'}
                options={fieldConfig.options}
                placeholder='Value'
                portal
                value={value}
                onChange={onChange}
              />
            ) : (
              <Input
                disabled={!editable}
                id={`${idPrefix}__filter-value-${index}`}
                placeholder='Value'
                type={['Integer', 'Float'].includes(fieldConfig.type) ? 'number' : 'string'}
                value={value}
                onChange={onChange}
              />
            )}
          </CriterionValue>
        )
      }
    />
    <Controller
      control={control}
      name={`criteria.${index}.unit`}
      rules={{
        required: (fieldConfig?.units ?? []).length > 0,
      }}
      render={({ field: { value, onChange } }) =>
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fieldConfig?.units ?? []).length > 0 && (
          <CriterionValueUnit>
            <Select
              disabled={!editable}
              id={`${idPrefix}__filter-unit-${index}`}
              placeholder='Unit'
              value={value}
              onValueChange={onChange}
            >
              {fieldConfig.units.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </CriterionValueUnit>
        )
      }
    />
    <CriterionDeleteButton>
      <Button
        aria-label={`Delete criteria ${index + 1}`}
        disabled={!editable}
        id={`${idPrefix}__filter-delete-${index}`}
        type='button'
        variant='basic'
        onClick={onDelete}
      >
        <TrashIcon />
      </Button>
    </CriterionDeleteButton>
  </CriterionRow>
);

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[4]};
`;

const AddCriterionButton = styled.div`
  display: inline-block;
  margin-top: ${Spacing[4]};
`;

interface FilterConfigPanelProps {
  editable: boolean;
  filterConfigs: WorkflowFilterFieldConfig[];
  filterCriteria: TriggerRecurringAttributes['filter'];
  idPrefix: string;
  title: string;
  onOutsideClick: () => void;
  updateFilterCriteria: (filter: TriggerRecurringAttributes['filter']) => void;
}

const WorkflowObjectFilterConfigPanel: VFC<FilterConfigPanelProps> = ({
  editable,
  filterConfigs,
  filterCriteria,
  idPrefix,
  title,
  onOutsideClick,
  updateFilterCriteria,
}) => {
  const { control, getValues, handleSubmit, watch } = useFormSecurely<{
    criteria: TriggerRecurringAttributes['filter'];
  }>({
    defaultValues: {
      criteria: filterCriteria ?? [NEW_CRITERIA],
    },
  });
  const { append, fields, remove } = useFieldArray({ control, name: 'criteria' });

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit((form) => {
        updateFilterCriteria(form.criteria);
      })();
    });
    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, updateFilterCriteria]);

  const optionsForField = useMemo(
    () =>
      filterConfigs.map(({ name }) => ({
        label: name,
        value: name,
      })),
    [filterConfigs],
  );

  const configBy = useMemo(() => keyBy('name', filterConfigs), [filterConfigs]);
  const criteria = useWatch({ control, name: 'criteria' });

  const rows = fields.map((field, index) => (
    <FilterConfigRow
      key={field.id}
      control={control}
      editable={editable}
      idPrefix={idPrefix}
      index={index}
      criterionTargetOptions={optionsForField}
      fieldConfig={configBy[criteria[index]?.name]?.config}
      onDelete={() => {
        remove(index);
      }}
    />
  ));

  return (
    <MaximizablePanel
      outsideExceptionalIdPrefixes={[idPrefix]}
      title={title}
      onOutsideClickAndEscPress={onOutsideClick}
    >
      <Well>
        {getValues().criteria.length > 0 ? (
          <>
            <Label size='md' weight='bold'>
              Where
            </Label>
            {intersperse(rows, (i) => (
              <Fragment key={`separator-${i}`}>
                <Spacer height={2} />
                <Label size='md' weight='bold'>
                  And
                </Label>
              </Fragment>
            ))}
          </>
        ) : (
          <Paragraph size='md'>No Criteria</Paragraph>
        )}
        <AddCriterionButton>
          <Button
            aria-label='Add a criteria'
            disabled={!editable}
            id={`${idPrefix}__object-filter-control-panel-add`}
            variant='basic'
            onClick={() => append(NEW_CRITERIA)}
          >
            {getValues().criteria.length === 0 ? 'Add criteria' : 'And'}
          </Button>
        </AddCriterionButton>
      </Well>
    </MaximizablePanel>
  );
};

export default WorkflowObjectFilterConfigPanel;
